export const primitiveTypeOptions = Object.freeze([
  "text",
  "heading",
  "paragraph",
  "image",
  "avatar",
  "chip",
  "button",
  "divider",
]);

export const presetTypeOptions = Object.freeze(["doctor-finder-result"]);

export const supportedTypeOptions = Object.freeze([...primitiveTypeOptions, ...presetTypeOptions]);

export const skeletonAnimationOptions = Object.freeze(["wave", "pulse"]);

export const skeletonDefaultType = "image";
export const skeletonTypeMaxRepeat = 100;

const DEFAULT_REPEAT = 1;
const TYPE_REPEAT_PATTERN = /^([a-z0-9-]+)(?:@(\d+))?$/i;
const SKELETON_TYPE_BONE_LAYOUTS = Object.freeze({
  paragraph: Object.freeze(["--type-text", "--type-text", "--type-text"]),
  "doctor-finder-result": Object.freeze([
    "--type-image",
    "--type-heading",
    "--type-text",
    "--type-text",
    "--type-text",
    "--type-text",
    "--type-divider",
    "--type-button",
  ]),
});

/**
 * Parse `type` input string into a list of supported types.
 * Supports comma lists and repeat syntax (example: "text@3").
 *
 * @param {string} typeInput
 * @param {Object} options
 * @param {string[]} [options.supportedTypes]
 * @param {string} [options.fallback]
 * @returns {string[]}
 */
export function parseSkeletonTypes(typeInput, options = {}) {
  const { supportedTypes = supportedTypeOptions, fallback = skeletonDefaultType } = options;
  const supportedTypesSet = toSupportedTypesSet(supportedTypes);

  const parsedTypes = splitTypeChunks(typeInput).flatMap((chunk) => parseTypeChunk(chunk, supportedTypesSet));

  return parsedTypes.length > 0 ? parsedTypes : [fallback];
}

/**
 * Validate full `type` input string.
 * Supports comma lists and optional @N repetition per chunk.
 *
 * @param {string} typeInput
 * @param {Object} options
 * @param {string[]} [options.supportedTypes]
 * @param {number} [options.maxRepeat]
 * @returns {boolean}
 */
export function isValidSkeletonTypeInput(typeInput, options = {}) {
  const { supportedTypes = supportedTypeOptions, maxRepeat = skeletonTypeMaxRepeat } = options;
  const supportedTypesSet = toSupportedTypesSet(supportedTypes);
  const chunks = splitTypeChunks(typeInput);

  return chunks.length > 0 && chunks.every((chunk) => isValidTypeChunk(chunk, supportedTypesSet, maxRepeat));
}

/**
 * @param {string} typeInput
 * @param {Object} options
 * @param {string[]} [options.supportedTypes]
 * @param {string} [options.fallback]
 * @returns {{type: string, bones: string[]}[]}
 */
export function resolveSkeletonRenderItems(typeInput, options = {}) {
  return parseSkeletonTypes(typeInput, options).map((type) => ({
    type,
    bones: resolveTypeBones(type),
  }));
}

// For a type, return its bone classes.
function resolveTypeBones(type) {
  return SKELETON_TYPE_BONE_LAYOUTS[type] || [`--type-${type}`];
}

// Parse one chunk ("text" or "text@3") and expand repeats.
function parseTypeChunk(typeChunk, supportedTypesSet) {
  const parsedChunk = parseTypeChunkParts(typeChunk);
  if (!parsedChunk) {
    return [];
  }

  const { type, repeat } = parsedChunk;
  if (!supportedTypesSet.has(type)) {
    return [];
  }

  if (!isValidRepeat(repeat, skeletonTypeMaxRepeat)) {
    return [];
  }

  return Array.from({ length: repeat }, () => type);
}

// Validate one chunk ("text" or "text@3"), with repeat limit enforcement.
function isValidTypeChunk(typeChunk, supportedTypesSet, maxRepeat) {
  const parsedChunk = parseTypeChunkParts(typeChunk);
  if (!parsedChunk) {
    return false;
  }

  const { type, repeat } = parsedChunk;
  if (!supportedTypesSet.has(type)) {
    return false;
  }

  return isValidRepeat(repeat, maxRepeat);
}

function toSupportedTypesSet(supportedTypes) {
  return new Set(supportedTypes.map((type) => String(type).toLowerCase()));
}

function splitTypeChunks(typeInput) {
  return String(typeInput ?? "")
    .split(",")
    .map((chunk) => chunk.trim())
    .filter(Boolean);
}

function parseTypeChunkParts(typeChunk) {
  const parsedChunk = String(typeChunk).match(TYPE_REPEAT_PATTERN);
  if (!parsedChunk) {
    return null;
  }

  const [, typeValue = "", repeatValue] = parsedChunk;
  return {
    type: typeValue.toLowerCase(),
    repeat: repeatValue ? Number.parseInt(repeatValue, 10) : DEFAULT_REPEAT,
  };
}

function isValidRepeat(repeat, maxRepeat = Infinity) {
  return Number.isInteger(repeat) && repeat >= 1 && repeat <= maxRepeat;
}
